import {
  type FocusEventHandler,
  type KeyboardEvent as ReactKeyboardEvent,
  type Ref,
} from 'react';
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchIcon from '@mui/icons-material/Search';

interface SampleIDFieldProps {
  name?: string;
  value: string;
  error?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onOpen?: () => void;
  onClear?: () => void;
  dialogOpen?: boolean;
  dialogId?: string;
  locked?: boolean;
  lockedTooltip?: string;
  placeholder?: string;
}

const SampleIDField = ({
  name,
  value,
  error = false,
  inputRef,
  onBlur,
  onOpen,
  onClear,
  dialogOpen = false,
  dialogId,
  locked = false,
  lockedTooltip = 'Sample assignment is read-only',
  placeholder = 'Select or register a sample',
}: SampleIDFieldProps) => {
  const interactive = !locked && Boolean(onOpen);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (
      interactive &&
      (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown')
    ) {
      event.preventDefault();
      onOpen?.();
    }
  };

  return (
    <TextField
      name={name}
      value={value}
      label="Sample ID"
      placeholder={placeholder}
      error={error}
      onBlur={onBlur}
      onClick={interactive ? onOpen : undefined}
      onKeyDown={handleKeyDown}
      inputRef={inputRef}
      fullWidth
      InputProps={{
        readOnly: true,
        inputProps: interactive
          ? {
              'aria-haspopup': 'dialog',
              'aria-expanded': dialogOpen,
              'aria-controls': dialogOpen ? dialogId : undefined,
            }
          : {
              'aria-readonly': true,
            },
        startAdornment: (
          <InputAdornment position="start">
            {interactive ? (
              <SearchIcon fontSize="small" color="action" />
            ) : (
              <Inventory2OutlinedIcon fontSize="small" color="action" />
            )}
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end" sx={{ mr: -0.75 }}>
            {interactive && value && onClear && (
              <Tooltip title="Clear sample selection">
                <IconButton
                  size="small"
                  aria-label="clear sample selection"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => {
                    event.stopPropagation();
                    onClear();
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {interactive ? (
              <ChevronRightIcon color="action" fontSize="small" />
            ) : (
              <Tooltip title={lockedTooltip}>
                <LockOutlinedIcon
                  color="action"
                  fontSize="small"
                  aria-label="sample assignment locked"
                />
              </Tooltip>
            )}
          </InputAdornment>
        ),
        sx: { cursor: interactive ? 'pointer' : 'text' },
      }}
      sx={{
        '& .MuiInputBase-input': {
          cursor: interactive ? 'pointer' : 'text',
        },
      }}
    />
  );
};

export default SampleIDField;
